import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import hexWithOpacity from "core-web/utils/hexWithOpacity";
import colors from "theme/config/colors";
import styled from "theme/libs/styled";
import ThemeButton from "theme/components/buttons/ThemeButton";
import ReviewEmpty from "theme/components/icons/ReviewEmpty";
import ReviewFull from "theme/components/icons/ReviewFull";
import TextStyles from "theme/components/text/TextStyles";
import LevelProgress from "./LevelProgress";

const LEVEL_IDS = {
    BRONZE: "bronze",
    SILVER: "silver",
    GOLD: "guld",
};

const LEVEL_NAMES = {
    BRONZE: "Brons",
    SILVER: "Silver",
    GOLD: "Guld",
};

const Wrapper = styled("div")`
    margin-bottom: 64px;

    ${({ theme }) => theme.below.desktopSm} {
        margin-bottom: 40px;
    }
`;

const OverviewWrapper = styled("div")`
    border-radius: 8px;
    padding: 42px 48px;
    display: grid;
    grid-template-columns: auto minmax(300px, auto);
    color: ${({ theme }) => theme.colors.darkGreen};
    gap: 50px;
    align-items: center;

    ${({ theme }) => theme.below.tabletSm} {
        grid-template-columns: 1fr;
        grid-template-rows: 1fr 1fr;
        gap: 35px;
    }

    &[data-level-id="${LEVEL_IDS.BRONZE}"] {
        background-color: ${({ theme }) => theme.colors.lightGreen};
    }

    &[data-level-id="${LEVEL_IDS.SILVER}"] {
        background-color: ${({ theme }) => theme.colors.lightBlue};
    }

    &[data-level-id="${LEVEL_IDS.GOLD}"] {
        background-color: ${({ theme }) => theme.colors.lightPink};
        color: ${({ theme }) => theme.colors.red};
    }

    ${({ theme }) => theme.below.tabletSm} {
        padding: 32px 16px;
    }
`;

const MembershipInfo = styled("div")`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
`;

const MembershipInfoInner = styled("div")`
    max-width: 340px;
    width: 100%;
`;

const InformationList = styled("div", {
    shouldForwardProps: (prop) => ["borderColor"].indexOf(prop) !== -1,
})`
    & > div {
        border-bottom: 1px solid ${({ borderColor }) => borderColor}; //rgba(0, 77, 66, 0.5);
    }
`;

const InformationItem = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding: 12px 0;
`;

const LevelIconWrapper = styled("div")`
    display: flex;
    gap: 8px;
`;

const LevelName = styled(TextStyles)`
    text-align: center;
    margin-top: 8px;
`;

const NumberOfPoints = styled(TextStyles)`
    text-align: center;
    margin-top: 4px;
`;

const ButtonWrapper = styled("div")`
    width: 100%;
    max-width: 340px;
    margin-top: 20px;
`;

/**
 * Get percentage depending on how many points is left to next level.
 *
 * @param {number} points - Current rewardPoints user has
 * @returns {number} Number between 0 and 100
 */
const getPercentage = (points) => {
    // Bronze
    if (points < 2000) {
        return Math.floor((points / 2000) * 100);
    }

    // Silver
    if (points >= 2000 && points < 4000) {
        return Math.floor(((points - 2000) / 2000) * 100);
    }

    // Gold
    if (points >= 4000) {
        return 100;
    }

    return 0;
};

/**
 * Return number of days until `dateString` as string.
 *
 * @param {string} dateString
 * @param {function} t Translation function from `useTranslation`
 * @returns {string} Date formatted as `x days`
 */
const formatExpiryDate = (dateString, t) => {
    const expiryDate = new Date(dateString).getTime();
    const currentDate = new Date(Date.now()).getTime();

    const oneDayMs = 1000 * 60 * 60 * 24;
    const timeDiff = expiryDate - currentDate;
    const numberOfDaysDiff = Math.floor(timeDiff / oneDayMs);

    return `${numberOfDaysDiff} ${numberOfDaysDiff === 1 ? t("day") : t("days")}`;
};

const MembershipOverview = ({ showReadMore }) => {
    const { t } = useTranslation();

    const { memberLevel } = useSelector((state) => state.user) ?? {};
    const { locale } = useSelector((state) => state?.application) ?? {};

    const {
        expires,
        rewardPoints,
        levelName: userLevelName,
        levelId: userLevelId,
        pointsToNextLevel,
    } = memberLevel ?? {};

    const levelId = userLevelId ?? LEVEL_IDS.BRONZE;
    const levelName = userLevelName ?? LEVEL_NAMES.BRONZE;

    let formattedRewardPoints = "0";
    let formattedPointsToNextLevel = "0";

    try {
        const formattedLocale = locale?.toLowerCase().replace("_", "-") ?? "sv-se";
        formattedRewardPoints = parseInt(rewardPoints, 10).toLocaleString(formattedLocale);
        formattedPointsToNextLevel = parseInt(pointsToNextLevel, 10).toLocaleString(formattedLocale);
    } catch (e) {
        formattedRewardPoints = `${rewardPoints}`;
        formattedPointsToNextLevel = `${pointsToNextLevel}`;
    }

    const informationListHeadingTextStyles = "Buenos Aires/14-120 SemiBold";
    const informationListTextStyles = "Buenos Aires/24-110 SemiBold";

    const fullIconsPerLevel = {
        [LEVEL_IDS.BRONZE]: 1,
        [LEVEL_IDS.SILVER]: 2,
        [LEVEL_IDS.GOLD]: 3,
    };

    const maxNumberOfIcons = 3;
    const numberOfFullIconsArray = Array(fullIconsPerLevel[levelId] ?? 0).fill();
    const numberOfEmptyIconsArray = Array(maxNumberOfIcons - (fullIconsPerLevel[levelId] ?? 0)).fill();

    const isSilver = levelId === LEVEL_IDS.SILVER;
    const isGold = levelId === LEVEL_IDS.GOLD;

    const buttonTheme = isGold ? "red_outlined" : "dark_green_outlined";
    const backgroundColor = isGold ? colors.red : hexWithOpacity(colors.darkGreen, 20);
    const foregroundColor = isGold ? colors.red : colors.darkGreen;
    const borderColor = isGold ? colors.red : hexWithOpacity(colors.darkGreen, 50);
    const iconNotFilledColor = isGold ? colors.red : hexWithOpacity(colors.darkGreen, 30);

    const maxPointsForCurrentLevel = isSilver ? 4000 : 2000;

    const percentage = getPercentage(rewardPoints);

    return (
        <Wrapper>
            <OverviewWrapper data-level-id={levelId}>
                <LevelProgress
                    percentage={percentage}
                    backgroundColor={backgroundColor}
                    foregroundColor={foregroundColor}
                >
                    <LevelIconWrapper>
                        {numberOfFullIconsArray.map((_, i) => (
                            <ReviewFull
                                key={`icon_full_${i}`}
                                width="28px"
                                height="28px"
                                fill={foregroundColor}
                                stroke={foregroundColor}
                            />
                        ))}
                        {numberOfEmptyIconsArray.map((_, i) => (
                            <ReviewEmpty key={`icon_empty_${i}`} width="28px" height="28px" fill={iconNotFilledColor} />
                        ))}
                    </LevelIconWrapper>
                    <LevelName textStyles="Buenos Aires/32-120 SemiBold">{levelName}</LevelName>
                    <NumberOfPoints textStyles="Buenos Aires/14-120 SemiBold">
                        {isGold ? "+4000p" : `${formattedRewardPoints} / ${maxPointsForCurrentLevel} p`}
                    </NumberOfPoints>
                </LevelProgress>
                <MembershipInfo>
                    <MembershipInfoInner>
                        <TextStyles textStyles="Buenos Aires/32-120 SemiBold">
                            {t("member.membership_overview.heading")}
                        </TextStyles>
                        <InformationList borderColor={borderColor}>
                            <InformationItem>
                                <TextStyles textStyles={informationListHeadingTextStyles}>
                                    {t("member.membership_overview.current_points")}
                                </TextStyles>
                                <TextStyles textStyles={informationListTextStyles}>{formattedRewardPoints}</TextStyles>
                            </InformationItem>
                            <InformationItem>
                                <TextStyles textStyles={informationListHeadingTextStyles}>
                                    {t("member.membership_overview.next_level")}
                                </TextStyles>
                                <TextStyles textStyles={informationListTextStyles}>
                                    {formattedPointsToNextLevel}
                                </TextStyles>
                            </InformationItem>
                            {!!expires && (
                                <InformationItem>
                                    <TextStyles textStyles={informationListHeadingTextStyles}>
                                        {t("member.membership_overview.expires")}
                                    </TextStyles>
                                    <TextStyles textStyles={informationListTextStyles}>
                                        {formatExpiryDate(expires, t)}
                                    </TextStyles>
                                </InformationItem>
                            )}
                        </InformationList>
                    </MembershipInfoInner>
                    {showReadMore && (
                        <ButtonWrapper>
                            <ThemeButton buttonTheme={buttonTheme} as="a" href="/mina-sidor/mitt-medlemskap">
                                {t("read_more")}
                            </ThemeButton>
                        </ButtonWrapper>
                    )}
                </MembershipInfo>
            </OverviewWrapper>
        </Wrapper>
    );
};

MembershipOverview.propTypes = {
    showReadMore: PropTypes.bool,
};

MembershipOverview.defaultProps = {
    showReadMore: false,
};

export default MembershipOverview;
